import { Decimal } from 'decimal.js';
import {
  CellRendererSelectorResult,
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-enterprise';
import { VChip } from 'vuetify/lib';
import { alignCenter, alignLeft, alignRight, comparator, component } from './utils';
import {
  StatusResponse,
  VenueStatusResponse,
  getStatus,
  orderIntentLabel,
  roundingRuleAbbr,
  timeInForceAbbr,
} from '@/modules/marketplace/helpers/marketplace';
import MarketplaceOrdersTableActions from '@/modules/marketplace/components/MarketplaceOrdersTableActions.vue';
import { h } from 'vue';
import RateOutput from '@/modules/common/components/format-rate/RateOutput.vue';
import {
  OmsOrderStatus,
  OmsOrderType,
  OmsTimeInForceType,
  OrderIntent,
} from '@/connect/gen/consts/omsconsts_pb';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { FrontendOrder } from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { FrontendExecution } from '@/connect/gen/modules/apiengine/services/executions/executions_pb';
import { useSecuritiesStore } from '@/store/securities-store';
import { useCompaniesStore } from '@/store/companies-store';
import {
  OrderIntent as OrderIntentType,
  OrderStatus,
  OrderType,
} from '@/modules/marketplace/types/marketplace';
import { OmsOrder } from '@/modules/marketplace/models';
import { useAgreementsStore } from '@/store/agreements-store';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import {
  Agreement,
  AgreementStatus,
  ContractStatus,
  ExecutionStatus,
  SettlementType,
  availableCollateralTypes,
  availableCurrencies,
  availableExecutionsAndLoanStatuses,
  availableRoundingRules,
  formatCollateralMethod,
  formatCollateralType,
  formatCurrency,
  formatProfitCenter,
  formatSecurityCategoryString,
  formatSecurityTypeString,
  formatSettlementType,
  formatTermType,
  securityCategories,
  securityTypes,
  timeInForceTypes,
} from '@/connect';
import { SecurityCategory, SecurityType } from '@/connect/gen/consts/instrumentconsts_pb';
import {
  CustomDataType,
  agreementPopoverAdapter,
  orderTypeAdapter,
  prettyPrice,
  priceChangeAdapter,
  timestampAdapter,
  userIdentifier,
} from '@/modules/common/components/ag-table/columns/common';
import { RATE_PRECISION } from '@/modules/common/constants/precision';
import {
  decimalIndependentAmountRateAsPercentCollateralValueFormatter,
  enumValueFormatter,
  formatDecimalPercentage,
} from '@/modules/common/components/ag-table/columns/value-formatters';
import * as cols from './common';
import { filterDecimal, filterSelect, filterSelectMultiple, filterText } from './filter';
import { formatDate } from '@/modules/agreements/utils';

export { checkbox, settlementTypeString, settlementTypeEnum } from './common';

export const getStatusAdapter = <T>(): ((
  props:
    | {
        order: T;
        viewOrder?: (order: T) => void;
        label: (StatusResponse | VenueStatusResponse)['label'];
        color: (StatusResponse | VenueStatusResponse)['color'];
      }
    | undefined
) => undefined | CellRendererSelectorResult) =>
  component<{
    order: T;
    viewOrder?: (order: T) => void;
    label: (StatusResponse | VenueStatusResponse)['label'];
    color: (StatusResponse | VenueStatusResponse)['color'];
  }>(
    (props) => () =>
      h(
        VChip,
        {
          style: 'width: 108px',
          class: `justify-center text-lowercase`,
          props: { color: props.color, small: true },
          on: { click: () => props.viewOrder?.(props.order) },
        },
        props.label as string
      )
  );

export function status({
  viewOrder,
  filter,
}: {
  viewOrder: (orderRef: string) => void;
  filter?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'orderStatus',
    colId: 'orderStatus',
    headerName: 'Status',
    ...enumValueFormatter(OmsOrderStatus),
    cellRendererSelector: (params) => {
      return getStatusAdapter<FrontendOrder>()({
        order: params.data!,
        viewOrder: (order) => viewOrder(order.orderRef),
        ...getStatus({
          status: OmsOrderStatus[params.value] as OrderStatus,
          orderType: (params.data ? OmsOrderType[params.data?.orderType] : 'LIMIT') as OrderType,
          filled: Number(params.data?.totalExecQty),
        } as unknown as OmsOrder),
      });
    },
    resizable: false,
    pinned: 'left',
    minWidth: 118,
    width: 118,
    ...alignCenter(),
    ...(filter &&
      filterSelectMultiple({
        values: [
          {
            text: 'Open',
            value: { orderStatus: OmsOrderStatus.OPEN, hasExecuted: false },
          },
          {
            text: 'Held',
            value: { orderStatus: OmsOrderStatus.HELD, hasExecuted: false },
          },
          {
            text: 'Partial',
            value: { orderStatus: OmsOrderStatus.OPEN, hasExecuted: true },
          },
          {
            text: 'Filled',
            value: { orderStatus: OmsOrderStatus.FILLED },
          },
          {
            text: 'Expired',
            value: { orderStatus: OmsOrderStatus.EXPIRED },
          },
          {
            text: 'Canceled',
            value: { orderStatus: OmsOrderStatus.CANCELED },
          },
          {
            text: 'Force Canceled',
            value: { orderStatus: OmsOrderStatus.FORCE_CANCELED },
          },
        ],
        noSelectAll: true,
      })),
  };
}

export function side(config?: { filter?: boolean }): ColDef<FrontendOrder, Side | undefined> {
  return {
    field: 'side',
    colId: 'side',
    headerName: 'Side',
    ...enumValueFormatter(Side),
    cellRendererSelector: (params) =>
      cols.formatSideAdapter({
        side: params.value === Side.BORROW ? 'BORROW' : 'LOAN',
      }),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelect([
        { text: 'All', value: null },
        { text: 'Borrower', value: Side.BORROW },
        { text: 'Lender', value: Side.LOAN },
      ])),
  };
}

// this version of the "side" column does not use a Vue component
// so it's possible to customise the colors when hovering/selecting rows
// @TODO: maybe we can replace "side" with this one everywhere?
export function executionsSide(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return {
    field: 'side',
    colId: 'side',
    headerName: 'Side',
    valueFormatter: (params) => (params.value === Side.BORROW ? 'Borrower' : 'Lender'),
    cellClass: (params) => {
      return params.data?.acknowledgedAt
        ? params.value === Side.BORROW
          ? 'borrower'
          : 'lender'
        : '';
    },
    ...(config?.filter &&
      filterSelect([
        { text: 'All', value: null },
        { text: 'Borrower', value: Side.BORROW },
        { text: 'Lender', value: Side.LOAN },
      ])),
  };
}

export function cusip(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'cusip',
    colId: 'cusip',
    headerName: 'CUSIP',
    cellDataType: CustomDataType.CUSIP,
    ...alignLeft(),
    ...(config?.filter &&
      filterText({
        trim: true,
        uppercase: true,
        mode: 'contains',
      })),
  };
}

export function ticker(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    colId: 'ticker',
    headerName: 'Ticker',
    valueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.ticker || '–';
    },
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, uppercase: true })),
  };
}

export function securityName(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    colId: 'securityName',
    headerName: 'Security Name',
    valueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.name || '–';
    },
    initialWidth: 200,
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function securityType(config?: {
  filter?: boolean;
  hiddenByDefault?: boolean;
}): ColDef<FrontendOrder> {
  return {
    colId: 'securityTypes',
    headerName: 'Security Type',
    initialHide: !!config?.hiddenByDefault,
    valueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.securityType !== undefined
        ? formatSecurityTypeString(security.securityType)
        : '';
    },
    filterValueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.securityType !== undefined ? SecurityType[security.securityType] : '';
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: [...securityTypes.entries()]
          .map(([key, text]) => ({
            value: SecurityType[key],
            text,
          }))
          .sort((a, b) => a.text.localeCompare(b.text)),
        noSelectAll: true,
      })),
  };
}

export function securityCategory(config?: {
  filter?: boolean;
  hiddenByDefault?: boolean;
}): ColDef<FrontendOrder> {
  return {
    colId: 'securityCategories',
    headerName: 'Security Category',
    initialHide: !!config?.hiddenByDefault,
    valueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.securityCategory !== undefined
        ? formatSecurityCategoryString(security.securityCategory)
        : '';
    },
    filterValueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.securityCategory !== undefined
        ? SecurityCategory[security.securityCategory]
        : '';
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: [...securityCategories.entries()].map(([key, text]) => ({
          value: SecurityCategory[key],
          text,
        })),
        noSelectAll: true,
      })),
  };
}

export function isin(config?: {
  filter?: boolean;
  hiddenByDefault?: boolean;
}): ColDef<FrontendOrder> {
  return {
    colId: 'isin',
    headerName: 'ISIN',
    initialHide: !!config?.hiddenByDefault,
    valueGetter: (params: ValueGetterParams): string => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return security?.isin ?? '–';
    },
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, uppercase: true })),
  };
}

export function orderUnitPrice(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    colId: 'unitPrice',
    headerName: 'Unit Price',
    valueGetter: (params: ValueGetterParams): Decimal => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      return new Decimal(security?.lastClosePrice ?? '0');
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return `$${prettyPrice(params.value)}`;
    },
    useValueFormatterForExport: false,
    comparator: comparator.decimal,
    ...alignRight(),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function priceChange(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    colId: 'priceChange',
    headerName: 'Price Change',
    headerClass: 'header-center',
    enableCellChangeFlash: true,
    cellRendererSelector: (params) => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.data!.cusip);
      return priceChangeAdapter({
        lastClosePrice: new Decimal(security?.lastClosePrice ?? '0'),
        lastPrice: new Decimal(security?.lastPrice ?? '0'),
      });
    },
    useValueFormatterForExport: false,
    comparator: comparator.decimal,
    ...alignCenter(),
    minWidth: 135,
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export function openMarketValue(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    colId: 'openMarketValue',
    headerName: 'Open Market Value',
    valueGetter: (params: ValueGetterParams): Decimal => {
      const securitiesStore = useSecuritiesStore();
      const security = securitiesStore.getSecurity(params.getValue('cusip'));
      const dUnitPrice = new Decimal(security?.lastClosePrice ?? '0');
      const dOpenQuantity = new Decimal(Number(params.data.openQuantity));
      return dUnitPrice.mul(dOpenQuantity);
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return `$${prettyPrice(params.value, 0)}`;
    },
    useValueFormatterForExport: false,
    comparator: comparator.decimal,
    ...alignRight(),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

export function company(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  const companiesStore = useCompaniesStore();
  return {
    field: 'companyId',
    colId: 'companyId',
    headerName: 'Company',
    valueFormatter: (params) => {
      const cp = companiesStore.getCompany(params.value);
      return `${cp?.companyName} (${cp?.displayId})`;
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: companiesStore.companies.map(({ displayId, companyName }) => ({
          value: displayId,
          text: companyName,
        })),
      })),
  };
}

export function createdBy(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'createdBy',
    colId: 'createdBy',
    headerName: 'Created By',
    ...userIdentifier(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function lastUpdatedBy(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'lastUpdatedBy',
    colId: 'lastUpdatedBy',
    headerName: 'Last Updated By',
    ...userIdentifier(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function orderCreatedBy(config: {
  hiddenByDefault: boolean;
  filter?: boolean;
}): ColDef<FrontendExecution> {
  return {
    field: 'orderCreatedBy',
    colId: 'orderCreatedBy',
    headerName: 'Order Created By',
    ...userIdentifier(),
    initialHide: config.hiddenByDefault,
    ...alignLeft(),
    ...filterText({ trim: true, mode: 'contains' }),
  };
}

export function orderLastUpdatedBy(config: {
  hiddenByDefault: boolean;
  filter?: boolean;
}): ColDef<FrontendExecution> {
  return {
    field: 'orderLastUpdatedBy',
    colId: 'orderLastUpdatedBy',
    headerName: 'Order Last Updated By',
    ...userIdentifier(),
    initialHide: config.hiddenByDefault,
    ...alignLeft(),
    ...filterText({ trim: true, mode: 'contains' }),
  };
}

export function fee(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'fee',
    colId: 'fee',
    headerName: 'Fee',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export function rebate(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'rebate',
    colId: 'rebate',
    headerName: 'Rebate Rate',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export const openQuantity = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.quantity({ field: 'openQuantity', headerName: 'Open Qty', filter: config?.filter });

export const execQty = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.quantity({
    field: 'totalExecQty',
    colId: 'execQuantity',
    headerName: 'Exec Qty',
    dashWhenZero: true,
    filter: config?.filter,
  });

export const ioiQuantity = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.quantity({ field: 'ioiQuantity', headerName: 'IOI Qty', filter: config?.filter });

export const firmQuantity = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.quantity({ field: 'firmQuantity', headerName: 'Firm Qty', filter: config?.filter });

export const totalQuantity = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.quantity({
    field: 'quantity',
    colId: 'totalQuantity',
    headerName: 'Total Qty',
    filter: config?.filter,
  });

const avgExecutionRateAdapter = component<{
  rate: number | string | Decimal;
}>((props) => () => (!props.rate ? h('span', '–') : h(RateOutput, { props })));

export function avgExecFee(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'avgExecFee',
    colId: 'avgExecutionFee',
    headerName: 'Avg Exec Fee',
    cellRendererSelector: (params) =>
      avgExecutionRateAdapter({
        rate: params.value,
      }),
    valueFormatter: (params: ValueFormatterParams<FrontendOrder, string>) =>
      !params.value ? '' : new Decimal(params.value).toFixed(RATE_PRECISION),
    comparator: (a, b) => comparator.decimal(a?.avgExecutionFee, b?.avgExecutionFee),
    cellDataType: 'text',
    ...alignRight(),
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export function avgExecRebate(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'avgExecRebate',
    colId: 'avgExecutionRebate',
    headerName: 'Avg Exec Rebate',
    cellRendererSelector: (params) =>
      avgExecutionRateAdapter({
        rate: params.value,
      }),
    valueFormatter: (params: ValueFormatterParams<FrontendOrder, string>) =>
      !params.value ? '' : new Decimal(params.value).toFixed(RATE_PRECISION),
    comparator: (a, b) => comparator.decimal(a?.avgExecutionRebate, b?.avgExecutionRebate),
    cellDataType: 'text',
    ...alignRight(),
    ...(config?.filter && filterDecimal({ allowNegative: true })),
  };
}

export const boQuantity = (config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> =>
  cols.quantity({
    field: 'topOfBook.boQuantity',
    colId: 'boQuantity',
    headerName: 'Best Offer Qty',
    initialHide: true,
    dashWhenZero: true,
    filter: !config?.omitTopOfBookFiltersAndSorting && config?.filter,
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  });

export function boFee(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.boFee',
    colId: 'boFee',
    headerName: 'Best Offer Fee',
    initialHide: true,
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function boRebate(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.boRebate',
    colId: 'boRebate',
    headerName: 'Best Offer Rebate',
    initialHide: true,
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export const bbQuantity = (config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> =>
  cols.quantity({
    field: 'topOfBook.bbQuantity',
    colId: 'bbQuantity',
    headerName: 'Best Bid Qty',
    initialHide: true,
    dashWhenZero: true,
    filter: !config?.omitTopOfBookFiltersAndSorting && config?.filter,
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  });

export function bbFee(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bbFee',
    colId: 'bbFee',
    headerName: 'Best Bid Fee',
    initialHide: true,
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function bbRebate(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bbRebate',
    colId: 'bbRebate',
    headerName: 'Best Bid Rebate',
    initialHide: true,
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export const bboQuantity = (config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> =>
  cols.quantity({
    field: 'topOfBook.bboQuantity',
    colId: 'bboQuantity',
    headerName: 'BBO Qty',
    dashWhenZero: true,
    filter: !config?.omitTopOfBookFiltersAndSorting && config?.filter,
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  });

export function bboFee(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bboFee',
    colId: 'bboFee',
    headerName: 'BBO Fee',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function bboRebate(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bboRebate',
    colId: 'bboRebate',
    headerName: 'BBO Rebate',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function bboSpreadPctRebate(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bboSpreadPctRebate',
    colId: 'bboSpreadPctRebate',
    headerName: 'Spread% (Rebate)',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function bboSpreadVsFee(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bboSpreadVsFee',
    colId: 'bboSpreadVsFee',
    headerName: 'Spread vs. Best Bid (fee)',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function bboSpreadVsRebate(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  return {
    field: 'topOfBook.bboSpreadVsRebate',
    colId: 'bboSpreadVsRebate',
    headerName: 'Spread vs. Best Bid (rebate)',
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterDecimal({ allowNegative: true })),
    sortable: !config?.omitTopOfBookFiltersAndSorting,
  };
}

export function bboAgreements(config?: {
  filter?: boolean;
  omitTopOfBookFiltersAndSorting?: boolean;
}): ColDef<FrontendOrder> {
  const agreementsStore = useAgreementsStore();
  return {
    field: 'topOfBook.bboAgreementIds',
    colId: 'bboAgreementIds',
    headerName: 'Best Bid Agreements',
    cellRendererSelector: (params) => {
      return agreementPopoverAdapter({
        agreementIds: params.value,
        emptyValue: '-',
      });
    },
    sortable: false,
    ...alignLeft(),
    ...(!config?.omitTopOfBookFiltersAndSorting &&
      config?.filter &&
      filterSelectMultiple({
        values: agreementsStore.agreements
          .filter((agreement) => agreement.status === AgreementStatus.ACTIVE)
          .map(({ id, shortName }) => ({ value: id, text: shortName })),
      })),
  };
}

export const createTime = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.timestamp({
    field: 'createdAt',
    headerName: 'Create Time',
    filter: config?.filter && 'datetime',
  });

export const updateTime = (config?: { filter?: boolean }): ColDef<FrontendOrder> =>
  cols.timestamp({
    field: 'updatedAt',
    headerName: 'Update Time',
    filter: config?.filter && 'datetime',
  });

export const updatedAt = (config?: { filter?: boolean }): ColDef<FrontendExecution> =>
  cols.timestamp({
    field: 'updatedAt',
    headerName: 'Update Time',
    filter: config?.filter && 'datetime',
  });

export function myStatus(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return {
    field: 'status',
    colId: 'status',
    headerName: 'My Status',
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableExecutionsAndLoanStatuses.map(({ id, name, type }) => ({
          value: type === 'execution' ? ExecutionStatus[id] : ContractStatus[id],
          text: name,
        })),
      })),
  };
}

export function contraStatus(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return {
    field: 'contraStatus',
    colId: 'contraStatus',
    headerName: 'Contra Status',
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableExecutionsAndLoanStatuses.map(({ id, name, type }) => ({
          value: type === 'execution' ? ExecutionStatus[id] : ContractStatus[id],
          text: name,
        })),
      })),
  };
}

export const execTime = (config?: { filter?: boolean }): ColDef<FrontendExecution> =>
  cols.timestamp({
    field: 'execTime',
    headerName: 'Matched At',
    filter: config?.filter && 'datetime',
  });

export const acknowledgedAt = (config?: { filter?: boolean }): ColDef<FrontendExecution> =>
  cols.timestamp({
    field: 'acknowledgedAt',
    headerName: 'Acknowledged At',
    filter: config?.filter && 'datetime',
  });

export const bookingAccount = (config?: {
  hiddenByDefault?: boolean;
}): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'bookingAccount',
    headerName: 'Booking Account',
    valueGetter: (params) =>
      agreementsStore.getAgreement(params.data?.agreementId)?.bookingAccount?.trim() || '–',
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const counterpartyBookingAccount = (config?: {
  hiddenByDefault?: boolean;
}): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'counterpartyBookingAccount',
    headerName: 'Counterparty Booking Account',
    valueGetter: (params) =>
      agreementsStore.getAgreement(params.data?.agreementId)?.counterpartyBookingAccount?.trim() ||
      '–',
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const tradeType = (config?: { hiddenByDefault?: boolean }): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'tradeType',
    headerName: 'Term Type',
    valueGetter: (params) =>
      agreementsStore.getAgreement(params.data?.agreementId)?.tradeType || '–',
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const tradeDate = (config?: { filter?: boolean }): ColDef<FrontendExecution> =>
  cols.date({ field: 'tradeDate', headerName: 'Trade Date', filter: config?.filter });

export const settlementDate = (config?: { filter?: boolean }): ColDef<FrontendExecution> =>
  cols.date({ field: 'settlementDate', headerName: 'Settlement Date', filter: config?.filter });

export function lastText(config?: {
  hiddenByDefault?: boolean;
  filter?: boolean;
}): ColDef<FrontendExecution> {
  return {
    field: 'cancelReason',
    colId: 'cancelReason',
    headerName: 'Last Text',
    valueFormatter: (params) => params.value ?? '–',
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
    ...(config?.filter && filterText({ mode: 'contains' })),
  };
}

export function counterparty(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  const companiesStore = useCompaniesStore();
  return {
    field: 'counterpartyId',
    colId: 'counterpartyId',
    headerName: 'Counterparty',
    valueFormatter: (params) => {
      const cp = companiesStore.getCompany(params.value);
      return `${cp?.companyName} (${cp?.displayId})`;
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: companiesStore.companies.map(({ displayId, companyName }) => ({
          value: displayId,
          text: companyName,
        })),
      })),
  };
}

export function profitCenter(config: {
  hiddenByDefault: boolean;
  filter?: boolean;
}): ColDef<FrontendExecution, string | undefined> {
  return {
    field: 'profitCenter',
    colId: 'profitCenter',
    headerName: 'Profit Center',
    valueFormatter: (params) => formatProfitCenter(params.value ?? undefined),
    initialHide: config.hiddenByDefault,
    ...alignLeft(),
    ...filterText({ mode: 'equals' }),
  };
}

export function agreementShortName(config?: {
  filter?: boolean;
}): ColDef<FrontendExecution, string> {
  const agreementsStore = useAgreementsStore();
  return {
    field: 'agreementId',
    colId: 'agreementShortName',
    valueFormatter: (params) => agreementsStore.getAgreement(params.value!)?.shortName ?? '–',
    headerName: 'Agreement Short Name',
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: agreementsStore.agreements
          .filter((agreement) => agreement.status === AgreementStatus.ACTIVE)
          .map(({ id, shortName }) => ({ value: id, text: shortName })),
      })),
  };
}

export function agreementDisplayId(config?: {
  filter?: boolean;
}): ColDef<FrontendExecution, string> {
  const agreementsStore = useAgreementsStore();
  return {
    field: 'agreementId',
    colId: 'agreementDisplayId',
    valueFormatter: (params) => agreementsStore.getAgreement(params.value!)?.id ?? '–',
    headerName: 'Agreement Ref',
    ...alignLeft(),
    ...(config?.filter && filterText({ mode: 'equals' })),
  };
}

export function executionId(config?: { filter?: boolean }): ColDef<FrontendExecution, string> {
  return {
    field: 'execId',
    colId: 'executionDisplayId',
    valueFormatter: (params) => params.value ?? '–',
    headerName: 'Execution Ref',
    ...alignLeft(),
    ...(config?.filter && filterText({ mode: 'equals' })),
  };
}

export const executedQuantity = (config?: { filter?: boolean }): ColDef<FrontendExecution> => {
  return {
    ...cols.quantity({
      field: 'execQty',
      colId: 'quantity',
      headerName: 'Exec Qty',
      dashWhenZero: true,
      filter: config?.filter,
    }),
  };
};

export function independentAmountRateAsPercentCollateral(config?: {
  filter?: boolean;
}): ColDef<Agreement, string> {
  return {
    field: 'collateralRate',
    colId: 'percentCollateral',
    headerName: 'Collateral Rate',
    ...decimalIndependentAmountRateAsPercentCollateralValueFormatter(),
    ...alignRight({ hasPostfix: true }),
    ...(config?.filter &&
      filterDecimal({
        allowNegative: false,
      })),
  };
}

export function roundingRule(config?: {
  hiddenByDefault?: boolean;
  filter?: boolean;
}): ColDef<FrontendExecution> {
  return {
    field: 'roundingRule',
    colId: 'roundingRule',
    headerName: 'Rounding',
    minWidth: 120,
    width: 120,
    valueFormatter: (params) => {
      return roundingRuleAbbr(params.value);
    },
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableRoundingRules.map(({ id, name }) => ({
          value: id,
          text: name,
        })),
      })),
  };
}

export const rateType = (): ColDef<FrontendExecution> => {
  return {
    colId: 'rateType',
    headerName: 'Rate Type',
    valueGetter: () => 'Rebate Rate',
    ...alignLeft(),
  };
};

export const pricingCurrency = (config?: { filter?: boolean }): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'pricingCurrency',
    headerName: 'Pricing Currency',
    valueGetter: (params) =>
      formatCurrency(agreementsStore.getAgreement(params.data?.agreementId)?.pricingCurrency),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableCurrencies.map(({ id, name }) => ({
          value: formatCurrency(id),
          text: name,
        })),
      })),
  };
};

export const billingCurrency = (config?: {
  hiddenByDefault?: boolean;
  filter?: boolean;
}): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'billingCurrency',
    headerName: 'Billing Currency',
    valueGetter: (params) => {
      return formatCurrency(
        agreementsStore.getAgreement(params.data?.agreementId)?.billingCurrency
      );
    },
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableCurrencies.map(({ id, name }) => ({
          value: formatCurrency(id),
          text: name,
        })),
      })),
  };
};

export function unitPrice(config?: {
  filter?: boolean;
  hiddenByDefault: boolean;
}): ColDef<FrontendExecution> {
  return cols.price({
    field: 'unitPrice',
    colId: 'unitPrice',
    headerName: 'Unit Price',
    filter: config?.filter,
    initialHide: config?.hiddenByDefault,
  });
}

export function settlementPrice(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return cols.price({
    field: 'collateralPrice',
    colId: 'collateralPrice',
    headerName: 'Collateral Price',
    filter: config?.filter,
  });
}

export const settlementAmount = (config?: { filter?: boolean }): ColDef<FrontendExecution> => {
  return cols.price({
    field: 'contractValue',
    headerName: 'Contract Value',
    filter: config?.filter,
  });
};

export function settlementType(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return {
    field: 'settlementType',
    colId: 'settlementType',
    headerName: 'Sett Type',
    valueFormatter: (params) => formatSettlementType(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: [SettlementType.NSCC, SettlementType.BILATERAL, SettlementType.OCC].map(
          (value) => ({
            value,
            text: formatSettlementType(value),
          })
        ),
      })),
  };
}

export const dividendRequired = (config?: {
  hiddenByDefault?: boolean;
}): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'dividendRequired',
    headerName: 'Dividend Required',
    valueGetter: (params) => {
      return formatDecimalPercentage(
        agreementsStore.getAgreement(params.data?.agreementId)?.dividendRequired,
        0
      );
    },
    initialHide: config?.hiddenByDefault,
    ...alignRight({ hasPostfix: true }),
  };
};

export const termType = (config?: { hiddenByDefault?: boolean }): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'termType',
    headerName: 'Term Type',
    valueGetter: (params) => {
      const executionTermType = agreementsStore.getAgreement(params.data?.agreementId)?.termType;
      return !executionTermType ? '–' : formatTermType(executionTermType);
    },
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const termStartDate = (config?: {
  hiddenByDefault?: boolean;
}): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'termStartDate',
    headerName: 'Term Start Date',
    cellRendererSelector: (params) =>
      timestampAdapter(
        formatDate(agreementsStore.getAgreement(params.data?.agreementId)?.termStartDate)
      ),
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const termEndDate = (config?: { hiddenByDefault?: boolean }): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'termEndDate',
    headerName: 'Term End Date',
    cellRendererSelector: (params) =>
      timestampAdapter(
        formatDate(agreementsStore.getAgreement(params.data?.agreementId)?.termEndDate)
      ),
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const collateralType = (config?: { filter?: boolean }): ColDef<FrontendExecution> => {
  return {
    field: 'collateralType',
    colId: 'collateralType',
    headerName: 'Collateral Type',
    valueFormatter: (params) => formatCollateralType(params.value!),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: availableCollateralTypes.map(({ id, name }) => ({
          value: id,
          text: name,
        })),
      })),
  };
};

export const collateralMethod = (config?: {
  hiddenByDefault?: boolean;
}): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'collateralMethod',
    headerName: 'Collateral Method',
    valueGetter: (params) => {
      const executionCollateralMethod = agreementsStore.getAgreement(
        params.data?.agreementId
      )?.collateralMethod;
      return !executionCollateralMethod ? '–' : formatCollateralMethod(executionCollateralMethod);
    },
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export const rwaBucket = (config?: { hiddenByDefault?: boolean }): ColDef<FrontendExecution> => {
  const agreementsStore = useAgreementsStore();
  return {
    colId: 'rwaBucket',
    headerName: 'RWA Bucket',
    valueGetter: (params) => {
      return agreementsStore.getAgreement(params.data?.agreementId)?.rwaBucket?.trim() || '–';
    },
    initialHide: config?.hiddenByDefault,
    ...alignLeft(),
  };
};

export function orderType(config?: { filter: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'orderType',
    colId: 'orderType',
    headerName: 'Type',
    ...enumValueFormatter(OmsOrderType),
    cellRendererSelector: (params) => {
      return orderTypeAdapter({
        orderType: OmsOrderType[params.value],
      });
    },
    ...alignLeft(),
    ...(config?.filter &&
      filterSelect([
        { text: 'All', value: null },
        { text: 'Limit', value: OmsOrderType.LIMIT },
        // Not yet supported
        // { text: 'Market', value: OmsOrderType.MARKET },
      ])),
  };
}

export function orderIntent(config?: { filter: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'orderIntent',
    colId: 'orderIntent',
    headerName: 'Intent',
    valueFormatter: (params) => orderIntentLabel(OrderIntent[params.value] as OrderIntentType),
    ...alignLeft(),
    ...(config?.filter &&
      filterSelect([
        { text: 'All', value: null },
        { text: 'Firm', value: OrderIntent.FIRM },
        { text: 'IOI', value: OrderIntent.IOI },
      ])),
  };
}

export function timeInForce(config?: { filter: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'timeInForceType',
    colId: 'timeInForce',
    headerName: 'Time in Force',
    valueFormatter: (params) => {
      return timeInForceAbbr(OmsTimeInForceType[params.value]);
    },
    ...alignCenter(),
    ...(config?.filter &&
      filterSelect<FrontendOrder, OmsTimeInForceType, OmsTimeInForceType | null>([
        { text: 'All', value: null },
        ...[...timeInForceTypes.entries()].map(([value, text]) => ({
          value,
          text,
        })),
      ])),
  };
}

export const goodTillTime = (config?: { filter: boolean }): ColDef<FrontendOrder> =>
  cols.time({ field: 'goodTillTime', headerName: 'GTT', filter: config?.filter });

export function minQuantity(config?: { filter: boolean }): ColDef<FrontendOrder> {
  return {
    ...cols.quantity({
      field: 'minQuantity',
      colId: 'minQuantity',
      headerName: 'Min. Quantity',
      dashWhenZero: true,
      filter: config?.filter,
    }),
    valueFormatter: (params: ValueFormatterParams<FrontendOrder, bigint>) => {
      if (!params.value || params.value === 1n) {
        return '–';
      }

      return formatPrettyNumber(params.value ?? 0n);
    },
  };
}

export function agreements(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  const agreementsStore = useAgreementsStore();
  return {
    field: 'agreementIds',
    colId: 'agreementIds',
    headerName: 'Agreements',
    cellRendererSelector: (params) =>
      agreementPopoverAdapter({
        agreementIds: params.value,
      }),
    sortable: false,
    ...alignLeft(),
    ...(config?.filter &&
      filterSelectMultiple({
        values: agreementsStore.agreements
          .filter((agreement) => agreement.status === AgreementStatus.ACTIVE)
          .map(({ id, shortName }) => ({ value: id, text: shortName })),
      })),
  };
}

export function orderRef(config?: { filter?: boolean }): ColDef<FrontendOrder> {
  return {
    field: 'orderRef',
    colId: 'orderRef',
    headerName: 'Order Ref',
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'contains' })),
  };
}

export function orderDisplayId(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return {
    field: 'orderDisplayId',
    colId: 'orderDisplayId',
    headerName: 'Order Ref',
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'equals' })),
  };
}

export function auroraLoanID(config?: { filter?: boolean }): ColDef<FrontendExecution> {
  return {
    field: 'tradeId',
    colId: 'tradeId',
    headerName: 'Contract ID',
    ...alignLeft(),
    ...(config?.filter && filterText({ trim: true, mode: 'equals' })),
  };
}

export function percentagePar(config?: {
  filter?: boolean;
  hiddenByDefault?: boolean;
}): ColDef<FrontendExecution> {
  return {
    colId: 'percentagePar',
    headerName: '% of par',
    initialHide: !!config?.hiddenByDefault,
    valueGetter: (params) => {
      const val = params.data?.percentagePar;
      return val != null ? new Decimal(val) : null;
    },
    valueFormatter: (params) => (params.value != null ? prettyPrice(params.value) : '–'),
    comparator: comparator.decimal,
    ...alignRight(),
    ...(config?.filter && filterDecimal({ allowNegative: false })),
  };
}

const actionsAdapter = component<{
  order: FrontendOrder;
  viewOrder?: (orderRef: string) => void;
  editOrder?: (order: FrontendOrder) => void;
  actions?: string[];
}>((props) => () => h(MarketplaceOrdersTableActions, { props }));

export function actions({
  viewOrder,
  editOrder,
  actions = ['view', 'edit', 'route', 'unroute', 'cancel'],
}: {
  viewOrder?: (orderRef: string) => void;
  editOrder?: (order: FrontendOrder) => void;
  actions?: string[];
}): ColDef<FrontendOrder> {
  return {
    colId: 'actions',
    headerName: 'Actions',
    cellRendererSelector: (params) =>
      actionsAdapter({
        order: params.data!,
        viewOrder,
        editOrder,
        actions,
      }),
    pinned: 'right',
    lockVisible: true,
    width: 150,
    maxWidth: 150,
    suppressColumnsToolPanel: true,
    sortable: false,
    ...alignCenter(),
  };
}
